import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ShoppingCartIcon, UserIcon, ChevronRight, Menu, X, Facebook, Instagram, Phone, Mail } from 'lucide-react';
interface HeaderProps {
  variant?: 'light' | 'dark' | 'solid';
}
export function Header({
  variant = 'light'
}: HeaderProps) {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const textColor = variant === 'light' ? 'text-white' : 'text-brand-grey';
  const hoverColor = variant === 'light' ? 'hover:text-brand-pink' : 'hover:text-brand-pink';
  const logoSrc = variant === 'light' ? 'https://crudemomultisite.kinsta.cloud/wine-theme-1/wp-content/uploads/sites/2/2025/10/PH-logo-white-web.png' : 'https://crudemomultisite.kinsta.cloud/wine-theme-1/wp-content/uploads/sites/2/2025/10/web-logo.png';
  // Determine header positioning and background based on variant
  const headerClasses = variant === 'solid' ? 'relative bg-white pt-5' : 'absolute top-0 left-0 right-0 bg-transparent pt-5';
  return <>
      <header className={`z-50 ${headerClasses}`} data-id="element-57">
        <div className="max-w-7xl mx-auto px-8" data-id="element-58">
          <div className="flex items-center justify-between h-12" data-id="element-59">
            {/* Logo */}
            <div className="flex items-center" data-id="element-60">
              <Link to="/" data-id="element-61">
                <img src={logoSrc} alt="Peterson House Logo" className="h-12 md:h-16" data-id="element-62" />
              </Link>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-8" data-id="element-63">
              <Link to="/cellar-door" className={`${textColor} ${hoverColor} text-base transition-colors`} data-id="element-64">
                Cellar Door
              </Link>
              <Link to="/restaurant" className={`${textColor} ${hoverColor} text-base transition-colors`} data-id="element-65">
                Restaurant
              </Link>
              <Link to="/wine-club" className={`${textColor} ${hoverColor} text-base transition-colors`} data-id="element-66">
                Wine Club
              </Link>
              <Link to="/weddings" className={`${textColor} ${hoverColor} text-base transition-colors`} data-id="element-67">
                Weddings & Events
              </Link>
              <Link to="/contact" className={`${textColor} ${hoverColor} text-base transition-colors`} data-id="element-68">
                Contact
              </Link>
            </nav>

            {/* Right Section */}
            <div className="flex items-center gap-4" data-id="element-69">
              <button className="hidden md:inline-flex items-center gap-2 bg-brand-pink text-white px-4 py-2 text-sm font-medium hover:bg-brand-pink/90 transition-colors rounded-none group" data-id="element-70">
                Shop Now
                <ChevronRight size={16} className="group-hover:translate-x-1 transition-transform duration-200" data-id="element-71" />
              </button>
              <button className={`hidden md:block ${textColor} ${hoverColor} transition-colors p-2`} data-id="element-72">
                <UserIcon className="w-5 h-5" data-id="element-73" />
              </button>
              <button className={`hidden md:block ${textColor} ${hoverColor} transition-colors p-2`} data-id="element-74">
                <ShoppingCartIcon className="w-5 h-5" data-id="element-75" />
              </button>

              {/* Mobile Menu Button */}
              <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className={`lg:hidden ${textColor} ${hoverColor} transition-colors p-2`} aria-label="Toggle menu" data-id="element-76">
                {mobileMenuOpen ? <X className="w-6 h-6" data-id="element-77" /> : <Menu className="w-6 h-6" data-id="element-78" />}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile Menu */}
      {mobileMenuOpen && <div className="fixed inset-0 z-40 lg:hidden" data-id="element-79">
          <div className="fixed inset-0 bg-black/80" onClick={() => setMobileMenuOpen(false)} data-id="element-80" />
          <nav className="fixed top-0 right-0 bottom-0 w-64 bg-neutral-900 pt-24 px-8 overflow-y-auto" data-id="element-81">
            <div className="flex flex-col space-y-6" data-id="element-82">
              <Link to="/cellar-door" className="text-white hover:text-brand-pink text-lg transition-colors" onClick={() => setMobileMenuOpen(false)} data-id="element-83">
                Cellar Door
              </Link>
              <Link to="/restaurant" className="text-white hover:text-brand-pink text-lg transition-colors" onClick={() => setMobileMenuOpen(false)} data-id="element-84">
                Restaurant
              </Link>
              <Link to="/wine-club" className="text-white hover:text-brand-pink text-lg transition-colors" onClick={() => setMobileMenuOpen(false)} data-id="element-85">
                Wine Club
              </Link>
              <Link to="/weddings" className="text-white hover:text-brand-pink text-lg transition-colors" onClick={() => setMobileMenuOpen(false)} data-id="element-86">
                Weddings & Events
              </Link>
              <Link to="/contact" className="text-white hover:text-brand-pink text-lg transition-colors" onClick={() => setMobileMenuOpen(false)} data-id="element-87">
                Contact
              </Link>

              <div className="pt-6 border-t border-neutral-700" data-id="element-88">
                <button className="w-full inline-flex items-center justify-center gap-2 bg-brand-pink text-white px-6 py-3 hover:bg-brand-pink/90 transition-colors rounded-none group mb-4" data-id="element-89">
                  Shop Now
                  <ChevronRight size={16} className="group-hover:translate-x-1 transition-transform duration-200" data-id="element-90" />
                </button>

                <div className="flex gap-4 justify-center mb-6" data-id="element-91">
                  <button className="text-white hover:text-brand-pink transition-colors p-2" data-id="element-92">
                    <UserIcon className="w-5 h-5" data-id="element-93" />
                  </button>
                  <button className="text-white hover:text-brand-pink transition-colors p-2" data-id="element-94">
                    <ShoppingCartIcon className="w-5 h-5" data-id="element-95" />
                  </button>
                </div>

                {/* Social Media Icons */}
                <div className="flex gap-4 justify-center mb-6 pb-6 border-b border-neutral-700" data-id="element-96">
                  <a href="https://facebook.com" target="_blank" rel="noopener noreferrer" className="text-white hover:text-brand-pink transition-colors p-2" data-id="element-97">
                    <Facebook className="w-5 h-5" data-id="element-98" />
                  </a>
                  <a href="https://instagram.com" target="_blank" rel="noopener noreferrer" className="text-white hover:text-brand-pink transition-colors p-2" data-id="element-99">
                    <Instagram className="w-5 h-5" data-id="element-100" />
                  </a>
                </div>

                {/* Contact Information */}
                <div className="space-y-4" data-id="element-101">
                  <a href="tel:+61249987881" className="flex items-center gap-3 text-white hover:text-brand-pink transition-colors" data-id="element-102">
                    <Phone className="w-5 h-5 flex-shrink-0" data-id="element-103" />
                    <span className="text-sm" data-id="element-104">(02) 4998 7881</span>
                  </a>
                  <a href="mailto:info@petersonhouse.com.au" className="flex items-center gap-3 text-white hover:text-brand-pink transition-colors" data-id="element-105">
                    <Mail className="w-5 h-5 flex-shrink-0" data-id="element-106" />
                    <span className="text-sm" data-id="element-107">info@petersonhouse.com.au</span>
                  </a>
                </div>
              </div>
            </div>
          </nav>
        </div>}
    </>;
}